import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  F0Box,
  SurveyFormBuilder,
  SurveyFormBuilderElement,
  QuestionType,
  UseFileUpload,
  useAiChat,
} from '@factorialco/f0-react'
import { FormDetailFragment } from 'generated/resources/graphql'
import { isEqual } from 'lodash'

import Loading from 'modules/core/components/Loading'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useOne } from 'modules/ai/lib/useOne'
import {
  FromFormToQuestionnaire,
  FromQuestionaireToMutationVariables,
  FromQuestionnaireToForm,
  forceMandatoryOnQuestions,
} from 'modules/forms/components/newFormEditor/helpers'
import { useUpdateNewForm } from 'modules/forms/hooks/useForms'
import { useResourceHeader } from 'modules/layout/components/ResponsivePage/hooks'

import { FormEditorActions } from './formEditorActions'
import { AgentStateType, QuestionnaireType } from './types'

// Knowledge Test surveys are restricted to single- and multi-choice questions
// (sections are always allowed by SurveyFormBuilder regardless of this list).
// Other question types are intentionally unavailable in the palette.
const KNOWLEDGE_TEST_ALLOWED_QUESTION_TYPES: QuestionType[] = ['select', 'multi-select']

// Knowledge Tests are identified by the form's backend formType, not by the
// One agent label (e.g. 'training-course-survey'), which is shared by all
// training surveys regardless of whether they are knowledge tests.
const isKnowledgeTestForm = (form: FormDetailFragment | null) => form?.formType === 'knowledge'

function getSectionsCount(questionnaire: QuestionnaireType): number {
  return questionnaire.elements.filter((element) => element.type === 'section').length
}

function getQuestionsCount(questionnaire: QuestionnaireType): number {
  const rootQuestions = questionnaire.elements.filter(
    (element) => element.type !== 'section'
  ).length
  const sectionQuestions = questionnaire.elements
    .filter((element) => element.type === 'section')
    .flatMap((section) => section.elements.length)
  return rootQuestions + sectionQuestions.reduce((acc, curr) => acc + curr, 0)
}

export interface FormEditorLabels {
  questionsLabel?: string
  sectionsLabel?: string
  saveSuccessMessage?: string
  saveErrorMessage?: string
  initialMessage?: string
}

interface FormEditorProps {
  form: FormDetailFragment | null
  initialQuestionnaire: QuestionnaireType
  resourceType: string
  formType?: AgentStateType['formType']
  allowedQuestionTypes?: QuestionType[]
  useUpload?: UseFileUpload
  labels?: FormEditorLabels
  requiredSkills?: string[]
  /**
   * When false, disables the AI co-creation integration: the One chat is not
   * mounted and the questionnaire is managed in local state. Defaults to true
   * to preserve the existing behavior for callers (e.g. engagement surveys).
   */
  coCreationEnabled?: boolean
}

export const FormEditor = (props: FormEditorProps) => {
  const { coCreationEnabled = true, form, initialQuestionnaire } = props

  // For Knowledge Test forms force mandatory=true on the initial elements so
  // legacy data with optional questions is normalized on first render and the
  // editor never displays a state that contradicts the form's contract.
  const isKnowledgeTest = isKnowledgeTestForm(form)
  const normalizedInitialQuestionnaire = useMemo<QuestionnaireType>(
    () =>
      isKnowledgeTest
        ? {
            ...initialQuestionnaire,
            elements: forceMandatoryOnQuestions(initialQuestionnaire.elements),
          }
        : initialQuestionnaire,
    [initialQuestionnaire, isKnowledgeTest]
  )

  const normalizedProps: FormEditorProps = {
    ...props,
    initialQuestionnaire: normalizedInitialQuestionnaire,
  }

  return coCreationEnabled ? (
    <FormEditorWithCoCreation {...normalizedProps} />
  ) : (
    <FormEditorLocal {...normalizedProps} />
  )
}

/**
 * Controller variant that wires the editor to the One co-creation agent.
 * Calls useOne / useAiChat — only safe to render when co-creation is enabled.
 */
const FormEditorWithCoCreation = ({
  form,
  initialQuestionnaire,
  formType,
  allowedQuestionTypes,
  useUpload,
  labels,
  requiredSkills = ['surveys'],
}: FormEditorProps) => {
  const { setOpen: setAiChatOpen } = useAiChat()

  useEffect(() => {
    return () => {
      setAiChatOpen(false)
    }
  }, [setAiChatOpen])

  const initialAgentState: AgentStateType = useMemo(
    () => ({
      formType,
      questionnaire: initialQuestionnaire,
      updateForm: true,
    }),
    [formType, initialQuestionnaire]
  )

  const oneOptions = {
    autoOpenChat: true as const,
    initialMessage: labels?.initialMessage ?? i18n.t('forms.one.chat_initial_message'),
    fullscreen: false,
  }

  const {
    coCreationData: agentState,
    setCoCreationData: setAgentState,
    isThinking,
  } = useOne<AgentStateType>(requiredSkills, initialAgentState, oneOptions)

  const setQuestionnaire = useCallback(
    (questionnaire: QuestionnaireType) => {
      if (!agentState || !setAgentState) return
      setAgentState({ ...agentState, questionnaire })
    },
    [agentState, setAgentState]
  )

  if (!agentState) {
    return <Loading expand />
  }

  return (
    <FormEditorView
      form={form}
      initialQuestionnaire={initialQuestionnaire}
      questionnaire={agentState.questionnaire}
      setQuestionnaire={setQuestionnaire}
      allowedQuestionTypes={allowedQuestionTypes}
      useUpload={useUpload}
      labels={labels}
      isThinking={isThinking}
    />
  )
}

/**
 * Controller variant that manages questionnaire state locally.
 * Does NOT call useOne / useAiChat — safe when co-creation is disabled.
 */
const FormEditorLocal = ({
  form,
  initialQuestionnaire,
  allowedQuestionTypes,
  useUpload,
  labels,
}: FormEditorProps) => {
  const [questionnaire, setQuestionnaire] = useState<QuestionnaireType>(initialQuestionnaire)

  // Reset when the underlying form id changes (initialQuestionnaire is memoized
  // upstream, so identity change implies a different form was loaded).
  useEffect(() => {
    setQuestionnaire(initialQuestionnaire)
  }, [initialQuestionnaire])

  return (
    <FormEditorView
      form={form}
      initialQuestionnaire={initialQuestionnaire}
      questionnaire={questionnaire}
      setQuestionnaire={setQuestionnaire}
      allowedQuestionTypes={allowedQuestionTypes}
      useUpload={useUpload}
      labels={labels}
      isThinking={false}
    />
  )
}

interface FormEditorViewProps {
  form: FormDetailFragment | null
  initialQuestionnaire: QuestionnaireType
  questionnaire: QuestionnaireType
  setQuestionnaire: (questionnaire: QuestionnaireType) => void
  allowedQuestionTypes?: QuestionType[]
  useUpload?: UseFileUpload
  labels?: FormEditorLabels
  isThinking: boolean
}

/**
 * Shared presentation: header sync, save bar, SurveyFormBuilder.
 * State is passed in via props from a controller (one of the two above).
 */
const FormEditorView = ({
  form,
  initialQuestionnaire,
  questionnaire,
  setQuestionnaire,
  allowedQuestionTypes,
  useUpload,
  labels,
  isThinking,
}: FormEditorViewProps) => {
  const { updateHeader, updateMetadataProperty } = useResourceHeader()
  const { updateForm } = useUpdateNewForm()

  const isKnowledgeTest = isKnowledgeTestForm(form)

  const [savedState, setSavedState] = useState<QuestionnaireType>(initialQuestionnaire)

  useEffect(() => {
    setSavedState(initialQuestionnaire)
  }, [initialQuestionnaire])

  useEffect(() => {
    updateHeader((header) => ({
      ...(header || {}),
      title: questionnaire.title,
      description: questionnaire.description,
    }))
    updateMetadataProperty(labels?.questionsLabel ?? i18n.t('surveys.header.metadata.questions'), {
      type: 'text' as const,
      content: `${getQuestionsCount(questionnaire)}`,
    })
    updateMetadataProperty(labels?.sectionsLabel ?? i18n.t('surveys.header.metadata.sections'), {
      type: 'text' as const,
      content: `${getSectionsCount(questionnaire)}`,
    })
  }, [questionnaire, updateHeader, updateMetadataProperty, labels])

  const isDirty = useMemo(() => !isEqual(questionnaire, savedState), [questionnaire, savedState])

  const onDiscard = () => {
    setQuestionnaire(initialQuestionnaire)
    setSavedState(initialQuestionnaire)
  }

  const onSave = async (): Promise<boolean> => {
    if (!form) return false

    const result = await updateForm({
      ...FromQuestionaireToMutationVariables(form.id, form.anonymous, questionnaire),
    })
    if (result.success) {
      setSavedState(questionnaire)
      return true
    } else {
      app.setFlash({
        type: 'wrong',
        message:
          labels?.saveErrorMessage ??
          i18n.t('trainings.surveys.create.edit_modal.form_error_message'),
      })
      return false
    }
  }

  const handleFormChange = (updatedElements: SurveyFormBuilderElement[]) => {
    const nextElements = FromFormToQuestionnaire(updatedElements)
    setQuestionnaire({
      ...questionnaire,
      elements: isKnowledgeTest ? forceMandatoryOnQuestions(nextElements) : nextElements,
    })
  }

  const formIsDraft = form?.status === 'draft'
  const elements = FromQuestionnaireToForm(questionnaire.elements)
  const effectiveAllowedQuestionTypes = isKnowledgeTest
    ? KNOWLEDGE_TEST_ALLOWED_QUESTION_TYPES
    : allowedQuestionTypes

  return (
    <F0Box width="full" height="full" padding="md" paddingBottom="5xl">
      <SurveyFormBuilder
        disabled={!formIsDraft}
        elements={elements}
        onChange={handleFormChange}
        allowedQuestionTypes={effectiveAllowedQuestionTypes}
        disallowOptionalQuestions={isKnowledgeTest}
        useUpload={useUpload}
        applyingChanges={isThinking}
      />
      {formIsDraft && (
        <FormEditorActions
          isDirty={isDirty}
          questionnaire={questionnaire}
          isKnowledgeTest={isKnowledgeTest}
          onSave={onSave}
          onDiscard={onDiscard}
          isThinking={isThinking}
        />
      )}
    </F0Box>
  )
}
